import { Spacing } from '@persona-id/design-tokens';
import styled from 'styled-components';
import { BooleanParam, useQueryParam } from 'use-query-params';

import { HitDetails } from 'dashboard/components/ReportCustomListDetails/HitDetails';
import ReportResultLayout from 'dashboard/components/ReportResult/ReportResultLayout';
import { ReportCustomList, ReportCustomListAttributes } from 'dashboard/models/report/custom-list';
import {
  ReportRunCustomList,
  ReportRunCustomListHitEntry,
  ReportRunMatchStatus,
} from 'dashboard/models/report-run';
import Sticker from 'design-system/components/Sticker';
import TabBar, { Tab } from 'design-system/components/TabBar';
import { Paragraph } from 'design-system/components/Text';
import { FontWeight } from 'design-system/variables/text';

interface ReportCustomListDetailsProps {
  report?: ReportCustomList;
  reportRun?: ReportRunCustomList;
  multiCardFormat?: boolean;
}

export const Bordered = styled.div`
  padding: ${Spacing[5]};
  border: 1px solid #e6e6ea;
  border-radius: ${({ theme }) => {
    return theme.borderRadius;
  }};
`;

export const ReportCustomListDetails = ({
  report,
  reportRun,
  multiCardFormat = false,
}: ReportCustomListDetailsProps) => {
  const attributes = reportRun ? reportRun.attributes : report?.attributes;
  const versionId = reportRun
    ? reportRun.relationships['report-template-version'].data.id
    : (attributes as ReportCustomListAttributes)['latest-published-version-id'];
  const { 'hit-entries': hits, 'dismissed-entries': dismissed } = attributes ?? {};
  const dismissedEntries = dismissed ?? [];
  const [isActiveTabSelected = true, setIsActiveTabSelected] = useQueryParam(
    'isActive',
    BooleanParam,
  );

  if (attributes?.result === ReportRunMatchStatus.Restricted) {
    return (
      <ReportResultLayout multiCardFormat={multiCardFormat}>
        <Paragraph>You do not have permission to view match results.</Paragraph>
      </ReportResultLayout>
    );
  }

  if (hits == null || (hits.length <= 0 && dismissedEntries.length <= 0)) {
    return (
      <ReportResultLayout multiCardFormat={multiCardFormat}>
        <Paragraph>No matches found</Paragraph>
      </ReportResultLayout>
    );
  }

  if (dismissedEntries.length <= 0) {
    return (
      <ReportResultLayout multiCardFormat={multiCardFormat}>
        {getHitDetails(hits, reportRun, versionId, multiCardFormat)}
      </ReportResultLayout>
    );
  }

  return (
    <>
      <StyledTabBar>
        <Tab
          id='report-run-custom-list__hits-tab-active'
          selected={isActiveTabSelected}
          onClick={() => {
            setIsActiveTabSelected(true);
          }}
        >
          <TabText>
            <span>Active</span>
            <Sticker>{hits.length}</Sticker>
          </TabText>
        </Tab>
        <Tab
          id='report-run-custom-list__hits-tab-dismissed'
          selected={!isActiveTabSelected}
          onClick={() => {
            setIsActiveTabSelected(false);
          }}
        >
          <TabText>
            <span>Dismissed</span>
            <Sticker>{dismissedEntries.length}</Sticker>
          </TabText>
        </Tab>
      </StyledTabBar>

      <ReportResultLayout multiCardFormat={multiCardFormat}>
        {isActiveTabSelected
          ? getHitDetails(hits, reportRun, versionId, multiCardFormat)
          : getHitDetails(dismissedEntries, reportRun, versionId, multiCardFormat)}
      </ReportResultLayout>
    </>
  );
};

const getHitDetails = (
  hits: ReportRunCustomListHitEntry[] | undefined,
  reportRun: ReportRunCustomList | undefined,
  versionId: string,
  multiCardFormat: boolean,
) => {
  if (hits == null || hits.length <= 0) {
    return <Paragraph>No matches found</Paragraph>;
  }

  return hits.map((hit, idx) => {
    return multiCardFormat ? (
      <HitDetails
        hit={hit}
        reportRun={reportRun}
        key={`${idx}-${reportRun?.id}`}
        versionId={versionId}
      />
    ) : (
      <Bordered>
        <HitDetails
          hit={hit}
          reportRun={reportRun}
          key={`${idx}-${reportRun?.id}`}
          versionId={versionId}
        />
      </Bordered>
    );
  });
};

const TabText = styled.div`
  font-weight: ${FontWeight.Medium};
  & > span {
    margin-right: ${Spacing[2]};
  }
`;

const StyledTabBar = styled(TabBar)`
  margin-bottom: ${Spacing[2]};
`;
